import { useCallback, useEffect, useState } from 'react'
import { NavigateOptions, useNavigate } from 'react-router-dom'

type PendingLeave = { path: string; options?: NavigateOptions }

/**
 * Confirm before leaving a form with unsaved changes (Keep editing / Discard).
 * Also warns on browser refresh / tab close while dirty.
 */
export const useUnsavedChangesGuard = () => {
  const navigate = useNavigate()
  const [dirty, setDirty] = useState(false)
  const [pendingLeave, setPendingLeave] = useState<PendingLeave | null>(null)

  useEffect(() => {
    if (!dirty) {
      return
    }
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [dirty])

  const requestLeave = useCallback(
    (path: string, options?: NavigateOptions) => {
      if (dirty) {
        setPendingLeave({ path, options })
      } else {
        navigate(path, options)
      }
    },
    [dirty, navigate]
  )

  const keepEditing = useCallback(() => setPendingLeave(null), [])

  const discard = useCallback(() => {
    if (pendingLeave) {
      navigate(pendingLeave.path, pendingLeave.options)
    }
    setPendingLeave(null)
    setDirty(false)
  }, [navigate, pendingLeave])

  return {
    setDirty,
    requestLeave,
    discardModalProps: { open: !!pendingLeave, onKeepEditing: keepEditing, onDiscard: discard },
  }
}
